import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Building2, Mail, Phone, MapPin, CalendarClock, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { ReferralTimeline } from './ReferralTimeline';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { value: '', label: 'All' },
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'matched', label: 'Matched' },
  { value: 'placed', label: 'Placed' },
  { value: 'closed', label: 'Closed' },
];

const FLOW = ['new', 'contacted', 'matched', 'placed', 'closed'];

const statusColor = (s) => ({
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-amber-100 text-amber-700',
  matched: 'bg-violet-100 text-violet-700',
  placed: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-slate-200 text-slate-600',
}[s] || 'bg-slate-100 text-slate-600');

export const AdminReferralsPanel = () => {
  const { token } = useAuth();
  const [referrals, setReferrals] = useState([]);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [noteById, setNoteById] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/referrals`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status },
      });
      setReferrals(res.data || []);
    } catch (e) {
      toast.error('Failed to load referrals');
    } finally { setLoading(false); }
  }, [status, token]);

  useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [load]);

  const updateStatus = async (r, newStatus) => {
    setBusyId(r.id);
    try {
      await axios.patch(`${API}/admin/referrals/${r.id}/status`, { status: newStatus, note: noteById[r.id] || null }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Marked as ${newStatus} — planner notified`);
      setNoteById((m) => ({ ...m, [r.id]: '' }));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally { setBusyId(null); }
  };

  return (
    <div data-testid="admin-referrals-panel">
      <div className="flex gap-1.5 flex-wrap mb-4">
        {STATUSES.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatus(s.value)}
            data-testid={`admin-referrals-filter-${s.value || 'all'}`}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${status === s.value ? 'bg-cn-primary text-white' : 'bg-slate-100 dark:bg-cn-ink-700 text-slate-600 dark:text-slate-300'}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cn-primary" /></div>
      ) : referrals.length === 0 ? (
        <div className="py-12 text-center text-slate-500 dark:text-slate-400" data-testid="admin-referrals-empty">
          No referrals yet. Discharge planners can submit referrals at <span className="font-medium">/for-discharge-planners</span>.
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-slate-500 dark:text-slate-400">{referrals.length} referral(s)</p>
          {referrals.map((r) => {
            const busy = busyId === r.id;
            const nextStatus = FLOW[Math.min(FLOW.indexOf(r.status) + 1, FLOW.length - 1)];
            return (
              <div key={r.id} data-testid={`admin-referral-${r.id}`} className="p-4 rounded-xl border border-slate-200 dark:border-cn-ink-700 bg-white dark:bg-cn-ink-800">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-slate-900 dark:text-white">{r.planner_name}</p>
                      {r.planner_role && <span className="text-xs text-slate-500 dark:text-slate-400">· {r.planner_role}</span>}
                      <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${statusColor(r.status)}`}>{r.status}</span>
                      {r.urgency === 'urgent' && (
                        <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-500 text-white inline-flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> Urgent
                        </span>
                      )}
                      {r.needs_attention && (
                        <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 inline-flex items-center gap-1" data-testid={`admin-referral-attention-${r.id}`}>
                          <AlertTriangle className="w-3 h-3" /> {r.attention_reason}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                      {r.organization && <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" /> {r.organization}</span>}
                      <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {r.email}</span>
                      {r.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {r.phone}</span>}
                    </div>
                  </div>
                  <div className="text-xs text-slate-400 whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</div>
                </div>

                <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-1.5 text-sm">
                  {r.patient_initials && <Field label="Patient">{r.patient_initials}</Field>}
                  {r.primary_diagnosis && <Field label="Diagnosis">{r.primary_diagnosis}</Field>}
                  {r.care_type && <Field label="Care type">{r.care_type}</Field>}
                  {(r.city || r.state) && <Field label="Location"><span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {[r.city, r.state].filter(Boolean).join(', ')}</span></Field>}
                  {r.discharge_date && <Field label="Discharge"><span className="inline-flex items-center gap-1"><CalendarClock className="w-3 h-3" /> {r.discharge_date}</span></Field>}
                  {r.hours_needed && <Field label="Hours">{r.hours_needed}</Field>}
                  {r.insurance && <Field label="Insurance">{r.insurance}</Field>}
                </div>

                {r.notes && <p className="mt-3 text-sm text-slate-600 dark:text-slate-300 italic">&ldquo;{r.notes}&rdquo;</p>}

                {(r.status_history || []).length > 0 && (
                  <details
                    className="mt-3"
                    data-testid={`admin-referral-history-${r.id}`}
                  >
                    <summary className="text-xs font-semibold text-slate-500 dark:text-slate-400 cursor-pointer hover:text-cn-primary select-none">
                      View lifecycle timeline ({r.status_history.length} event{r.status_history.length === 1 ? '' : 's'})
                    </summary>
                    <div className="mt-3 pl-1">
                      <ReferralTimeline
                        events={r.status_history}
                        testId={`admin-referral-timeline-${r.id}`}
                        title="Referral lifecycle"
                      />
                    </div>
                  </details>
                )}

                <input
                  value={noteById[r.id] || ''}
                  onChange={(e) => setNoteById((m) => ({ ...m, [r.id]: e.target.value }))}
                  placeholder="Optional note (sent to the planner with the status update)…"
                  data-testid={`admin-referral-note-${r.id}`}
                  className="mt-3 w-full text-sm rounded-lg border border-slate-200 dark:border-cn-ink-700 bg-white dark:bg-cn-ink-900 px-3 py-2 outline-none focus:border-cn-primary/40"
                />

                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  {r.status !== 'closed' && r.status !== nextStatus && (
                    <button
                      onClick={() => updateStatus(r, nextStatus)}
                      disabled={busy}
                      data-testid={`admin-referral-advance-${r.id}`}
                      className="cn-btn cn-btn-primary !py-1.5 !px-3 text-sm disabled:opacity-50"
                    >
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Mark {nextStatus}
                    </button>
                  )}
                  {r.status !== 'closed' && (
                    <button
                      onClick={() => updateStatus(r, 'closed')}
                      disabled={busy}
                      data-testid={`admin-referral-close-${r.id}`}
                      className="px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-100 dark:bg-cn-ink-700 text-slate-600 dark:text-slate-300 disabled:opacity-50"
                    >
                      Close
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <span className="text-xs text-slate-400 block">{label}</span>
    <span className="text-slate-800 dark:text-slate-200">{children}</span>
  </div>
);

export default AdminReferralsPanel;
